import { FormikProvider, useFormik } from 'formik'

import {
  BULK_UPDATE_USER_TRANSACTIONS_KEY,
  BulkUpdatePayload,
  bulkUpdateUserTransactions,
} from '@/actions/adminActions'
import { TRANSACTION_TYPE_OPTIONS } from '@/constants'
import { clearSelectedTransactions } from '@/reducers/admin/selectedTransactionsReducer'
import { getTransactionCategoryOptions } from '@/features/reports/reports.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { Alert, Button, Dialog, Form } from '@/ui'
import { getFieldNames, makeReqStringSchema } from '@/base'

export const BulkUpdateTransactionDialog = ({
  open,
  setOpen,
  disabled,
  onApply,
  selectedTransactionKeys,
  isSaving,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  disabled?: boolean
  onApply: (updatedTransactions?: Transaction[]) => void
  selectedTransactionKeys: number[]
  isSaving: boolean
}) => {
  const dispatch = useAppDispatch()

  const categoryOptions = useReselector(getTransactionCategoryOptions)
  const error = useReselector(getFetchError, BULK_UPDATE_USER_TRANSACTIONS_KEY)

  const formik = useFormik({
    initialValues: {
      transactionType: undefined as TransactionRuleCategoryType | undefined,
      transactionCategoryId: undefined as number | undefined,
      confirmTransactions: false,
    },
    onSubmit: async ({
      transactionCategoryId,
      transactionType,
      confirmTransactions,
    }) => {
      const data: BulkUpdatePayload = {
        transactionIds: selectedTransactionKeys,
        transactionCategoryId,
        type: transactionType,
        excludedAt: transactionType === 'business' ? null : Date.now(),
      }

      if (confirmTransactions) {
        data.confirmedAt = Date.now()
        data.requestedClarificationAt = null
        data.notifiedRequestedClarificationAt = null
      }

      const res = await bulkUpdateUserTransactions(data)(dispatch)
      if (res) {
        dispatch(clearSelectedTransactions())
        setOpen(false)
        onApply(res)
      }
    },
  })

  const { submitForm, isSubmitting, isValid } = formik

  const fieldNames = getFieldNames(formik)

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <Dialog.Trigger>
        <Button onClick={() => setOpen(true)} disabled={disabled}>
          Bulk Update
        </Button>
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Header>Update Transactions</Dialog.Header>
        <Dialog.Description>
          {selectedTransactionKeys.length} transactions selected
        </Dialog.Description>

        {error && <Alert variant="error">{error.message}</Alert>}

        <FormikProvider value={formik}>
          <Form.Select
            label="Transaction Type"
            name={fieldNames.transactionType}
            required
            options={TRANSACTION_TYPE_OPTIONS}
            schema={makeReqStringSchema()}
          />
          <Form.Select
            label="Transaction Category"
            name={fieldNames.transactionCategoryId}
            options={categoryOptions}
            searchable
            required
            schema={makeReqStringSchema()}
          />
          <Form.Checkbox
            name={fieldNames.confirmTransactions}
            label="Should Confirm Transactions?"
          />
        </FormikProvider>
        <Dialog.Footer>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submitForm}
            disabled={isSubmitting || !isValid || isSaving}
            loading={isSubmitting || isSaving}
          >
            Apply
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
